import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ===== COMPANIES =====
export type CompanyId = 'primary' | 'secondary' | 'third' | 'unassigned';

export interface Company {
  _id: string;
  code: CompanyId;
  name: string;
  shortName: string;
  displayName: string;   // Actual company name from CompanySettings
  logoUrl: string | null; // S3 key for company logo
  logoFileName: string | null;
  country: string;
  currency: string;
  vatEnabled: boolean;
  vatPercentage: number;
  settingsProfile: string;
  isActive: boolean;
  sortOrder: number;
  banks?: { code: number; name: string }[];
  stripeReconEnabled?: boolean;
  stripeClearingAccountId?: string | null;
}

// Use fetch with relative URL for Vite proxy compatibility
export const getCompanies = async (activeOnly: boolean = true): Promise<Company[]> => {
  const response = await fetch(`/api/accounting/companies?activeOnly=${activeOnly}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch companies');
  }
  return response.json();
};

export const getCompany = async (code: CompanyId): Promise<Company> => {
  const response = await fetch(`/api/accounting/companies/${code}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch company');
  }
  return response.json();
};

export const updateCompany = async (code: CompanyId, data: Partial<Company>) => {
  const response = await fetch(`/api/accounting/companies/${code}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error('Failed to update company');
  }
  return response.json();
};

export const seedCompanies = async () => {
  const response = await fetch('/api/accounting/companies/seed', {
    method: 'POST',
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to seed companies');
  }
  return response.json();
};


// ===== ACCOUNTS =====
export const getAccounts = async (params?: {
  type?: string;
  category?: string;
  isActive?: boolean;
  search?: string;
  companyId?: CompanyId;
}) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/accounts`, {
    params: { ...params, companyId: params?.companyId || 'primary' },
    withCredentials: true
  });
  return response.data;
};

export const getAccount = async (id: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/accounts/${id}`, {
    withCredentials: true
  });
  return response.data;
};

export const createAccount = async (data: any) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/accounts`, data, {
    withCredentials: true
  });
  return response.data;
};

export const updateAccount = async (id: string, data: any) => {
  const response = await axios.put(`${API_BASE_URL}/api/accounting/accounts/${id}`, data, {
    withCredentials: true
  });
  return response.data;
};

export const deleteAccount = async (id: string) => {
  const response = await axios.delete(`${API_BASE_URL}/api/accounting/accounts/${id}`, {
    withCredentials: true
  });
  return response.data;
};

export const getAccountBalance = async (id: string, startDate?: string, endDate?: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/accounts/${id}/balance`, {
    params: { startDate, endDate },
    withCredentials: true
  });
  return response.data;
};

export const getAccountTypesAndCategories = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/accounts/meta/types-categories`, {
    withCredentials: true
  });
  return response.data;
};

// ===== JOURNAL ENTRIES =====
export const getJournalEntries = async (params?: {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/journal-entries`, {
    params,
    withCredentials: true
  });
  return response.data;
};

export const getJournalEntry = async (id: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/journal-entries/${id}`, {
    withCredentials: true
  });
  return response.data;
};

export const createJournalEntry = async (data: any) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/journal-entries`, data, {
    withCredentials: true
  });
  return response.data;
};

export const updateJournalEntry = async (id: string, data: any) => {
  const response = await axios.put(`${API_BASE_URL}/api/accounting/journal-entries/${id}`, data, {
    withCredentials: true
  });
  return response.data;
};

export const postJournalEntry = async (id: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/journal-entries/${id}/post`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const voidJournalEntry = async (id: string, reason: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/journal-entries/${id}/void`,
    { reason },
    { withCredentials: true }
  );
  return response.data;
};

export const deleteJournalEntry = async (id: string) => {
  const response = await axios.delete(`${API_BASE_URL}/api/accounting/journal-entries/${id}`, {
    withCredentials: true
  });
  return response.data;
};

// ===== CURRENCIES =====
export const getCurrencies = async (isActive?: boolean) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/currencies`, {
    params: { isActive },
    withCredentials: true
  });
  return response.data;
};

export const getBaseCurrency = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/currencies/base`, {
    withCredentials: true
  });
  return response.data;
};

export const createCurrency = async (data: any) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/currencies`, data, {
    withCredentials: true
  });
  return response.data;
};

export const updateCurrency = async (code: string, data: any) => {
  const response = await axios.put(`${API_BASE_URL}/api/accounting/currencies/${code}`, data, {
    withCredentials: true
  });
  return response.data;
};

// ===== EXCHANGE RATES =====
export const getExchangeRates = async (params?: {
  fromCurrency?: string;
  toCurrency?: string;
  latest?: boolean;
}) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/currencies/exchange-rates`, {
    params,
    withCredentials: true
  });
  return response.data;
};

export const convertCurrency = async (from: string, to: string, amount: number, date?: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/currencies/exchange-rates/convert`, {
    params: { from, to, amount, date },
    withCredentials: true
  });
  return response.data;
};

export const createExchangeRate = async (data: any) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/currencies/exchange-rates`, data, {
    withCredentials: true
  });
  return response.data;
};

export const bulkCreateExchangeRates = async (rates: any[], effectiveDate?: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/currencies/exchange-rates/bulk`,
    { rates, effectiveDate },
    { withCredentials: true }
  );
  return response.data;
};

export const deleteExchangeRate = async (id: string) => {
  const response = await axios.delete(`${API_BASE_URL}/api/accounting/currencies/exchange-rates/${id}`, {
    withCredentials: true
  });
  return response.data;
};

// ===== ACCOUNTING PERIODS =====
export const getAccountingPeriods = async (params?: {
  fiscalYear?: number;
  status?: string;
}) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/periods`, {
    params,
    withCredentials: true
  });
  return response.data;
};

export const getCurrentPeriod = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/periods/current`, {
    withCredentials: true
  });
  return response.data;
};

export const getAccountingPeriod = async (id: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/periods/${id}`, {
    withCredentials: true
  });
  return response.data;
};

export const createAccountingPeriod = async (data: any) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/periods`, data, {
    withCredentials: true
  });
  return response.data;
};

export const createFiscalYear = async (fiscalYear: number, startMonth?: number) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/periods/fiscal-year`,
    { fiscalYear, startMonth },
    { withCredentials: true }
  );
  return response.data;
};

export const closePeriod = async (id: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/periods/${id}/close`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const reopenPeriod = async (id: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/periods/${id}/reopen`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const lockPeriod = async (id: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/periods/${id}/lock`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const deleteAccountingPeriod = async (id: string) => {
  const response = await axios.delete(`${API_BASE_URL}/api/accounting/periods/${id}`, {
    withCredentials: true
  });
  return response.data;
};

// ===== REPORTS =====
export const getTrialBalance = async (startDate?: string, endDate?: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/reports/trial-balance`, {
    params: { startDate, endDate },
    withCredentials: true
  });
  return response.data;
};

export const getBalanceSheet = async (asOfDate?: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/reports/balance-sheet`, {
    params: { asOfDate },
    withCredentials: true
  });
  return response.data;
};

export const getIncomeStatement = async (startDate: string, endDate: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/reports/income-statement`, {
    params: { startDate, endDate },
    withCredentials: true
  });
  return response.data;
};

export const getGeneralLedger = async (accountId: string, startDate?: string, endDate?: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/reports/general-ledger/${accountId}`, {
    params: { startDate, endDate },
    withCredentials: true
  });
  return response.data;
};

export const getDashboardSummary = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/reports/dashboard`, {
    withCredentials: true
  });
  return response.data;
};

export const getDailyActivityReport = async (params: {
  date: string;
  companyId?: string;
  userId?: string;
  hideSystem?: boolean;
}) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/reports/daily-activity`, {
    params: {
      date: params.date,
      companyId: params.companyId,
      userId: params.userId || undefined,
      hideSystem: params.hideSystem ? 'true' : 'false'
    },
    withCredentials: true
  });
  return response.data;
};

// ===== PERMISSIONS =====
export const getMyPermissions = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/permissions/me`, {
    withCredentials: true
  });
  return response.data;
};

export const getAllUsersPermissions = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/permissions`, {
    withCredentials: true
  });
  return response.data;
};

export const getUserPermissions = async (userId: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/accounting/permissions/${userId}`, {
    withCredentials: true
  });
  return response.data;
};

export const setUserPermissions = async (userId: string, permissions: any) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/permissions/${userId}`, permissions, {
    withCredentials: true
  });
  return response.data;
};

export const grantFullAccess = async (userId: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/permissions/${userId}/preset/full-access`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const grantReadOnlyAccess = async (userId: string) => {
  const response = await axios.post(`${API_BASE_URL}/api/accounting/permissions/${userId}/preset/read-only`, {}, {
    withCredentials: true
  });
  return response.data;
};

export const removeUserPermissions = async (userId: string) => {
  const response = await axios.delete(`${API_BASE_URL}/api/accounting/permissions/${userId}`, {
    withCredentials: true
  });
  return response.data;
};

